import { CKDrawer } from 'clearkit';
import React, { FC } from 'react';

import { SettingsCard } from '~/components/unified/SettingsCard';

import { LogInfo } from './LogInfo';
import { LogStatusCode } from './LogStatusCode';
import { LogTimeStamp } from './LogTimeStamp';
import { LogListItem } from './types';

type LogDetailsDrawerProps = {
  log: LogListItem | null;
};

const LogDetailItem = ({ label, value }: { label: string; value?: string }) => {
  return (
    <div className="overflow-hidden text-sm">
      <div className="mb-1 font-medium">{label}</div>
      <div className="overflow-hidden font-mono text-gray-600 text-ellipsis whitespace-nowrap">
        {value}
      </div>
    </div>
  );
};

export const LogDetailsDrawer: FC<LogDetailsDrawerProps> = ({ log }) => {
  return (
    <>
      <CKDrawer.Header>
        <CKDrawer.Heading className="capitalize">{log?.type}</CKDrawer.Heading>
        <LogInfo
          className="mb-4 text-lg"
          requestMethod={log?.requestMethod}
          requestPath={log?.requestPath}
        />
        <div className="flex justify-between text-lg">
          <LogStatusCode responseStatus={log?.responseStatus || 200} />
          <LogTimeStamp timestamp={log?.timestamp} />
        </div>
      </CKDrawer.Header>
      <CKDrawer.Body className="flex flex-col p-6 gap-6">
        <SettingsCard>
          <SettingsCard.Header>
            <SettingsCard.Heading>General</SettingsCard.Heading>
          </SettingsCard.Header>
          <SettingsCard.Body className="grid gap-5">
            <LogDetailItem label="Host" value={log?.requestHost} />
            <LogDetailItem label="Path" value={log?.requestPath} />
            <LogDetailItem label="API Version" value={log?.apiVersion} />
            <LogDetailItem label="Request IP" value={log?.requestIp} />
          </SettingsCard.Body>
        </SettingsCard>
        <SettingsCard>
          <SettingsCard.Header>
            <SettingsCard.Heading>Request Headers</SettingsCard.Heading>
          </SettingsCard.Header>
          <SettingsCard.Body className="grid gap-5">
            {log?.requestHeaders
              ? Object.keys(log.requestHeaders).map((key) => {
                  const value = log.requestHeaders
                    ? log.requestHeaders[key as keyof typeof log.requestHeaders]
                    : '';

                  return <LogDetailItem key={key} label={key} value={value} />;
                })
              : null}
          </SettingsCard.Body>
        </SettingsCard>
        <SettingsCard>
          <SettingsCard.Header>
            <SettingsCard.Heading>Response Headers</SettingsCard.Heading>
          </SettingsCard.Header>
          <SettingsCard.Body className="grid gap-5">
            {log?.responseHeaders
              ? Object.keys(log.responseHeaders).map((key) => {
                  const value = log.requestHeaders
                    ? log.responseHeaders[
                        key as keyof typeof log.responseHeaders
                      ]
                    : '';

                  return <LogDetailItem key={key} label={key} value={value} />;
                })
              : null}
          </SettingsCard.Body>
        </SettingsCard>
        <SettingsCard>
          <SettingsCard.Header>
            <SettingsCard.Heading>Response Body</SettingsCard.Heading>
          </SettingsCard.Header>
          <SettingsCard.Body>
            {log?.responseBody ? (
              <pre className="overflow-hidden font-mono text-sm text-gray-800">
                {log.responseBody}
              </pre>
            ) : (
              'Response body is empty'
            )}
          </SettingsCard.Body>
        </SettingsCard>
      </CKDrawer.Body>
    </>
  );
};
